import Checkbox from "@/common/components/Checkbox";
import Modal from "@/common/components/Modal";
import TextField from "@/common/components/TextField";
import useModalMessagesSimple from "@/common/hooks/useModalMessagesSimple";
import { FormTransformers } from "@/common/utils/formTransformers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { dashboardFilters } from "../../constants/dashboards/dashboardFilters.constant";
import { useUpdateDashboard } from "../../hooks/dashboards/useUpdateDashboard";
import { updateDashboardSchema, UpdateDashboardValidationSchema } from "../../schemas/dashboards/dashboard.schema";
import { DashboardData, DashboardFilters } from "../../types/dashboards/dashboard.type";

interface UpdateDashboardProps {
  dashboard: DashboardData;
  dashboardUpdatedAt: number;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
}

const UpdateDashboard = ({
  dashboard,
  dashboardUpdatedAt,
  isOpen,
  setIsOpen,
}: UpdateDashboardProps) => {
  const { setMessages } = useModalMessagesSimple({
    isError: true,
  });

  const queryClient = useQueryClient();

  const possibleFilters = useMemo(() => {
    return dashboard.components?.reduce((prev, component) => {
      const componentFilters = Object.keys(component.definition?.filters || {});

      componentFilters.forEach((filterKey) => {
        let mappedKey = filterKey;

        if (filterKey === 'start_date_gteq') {
          mappedKey = DashboardFilters.DateRange;
        }

        if (filterKey === 'end_date_lteq') {
          return;
        }

        const label = dashboardFilters[mappedKey as DashboardFilters];

        if (label && !prev[mappedKey]) {
          prev[mappedKey] = {
            value: mappedKey as DashboardFilters,
            label,
          };
        }
      });

      return prev;
    }, {} as Record<string, { value: DashboardFilters; label: string }>);
  }, [dashboardUpdatedAt]);

  const {
    register,
    handleSubmit,
    setError,
    reset,
    watch,
    setValue,
    getValues,
    formState: {
      errors,
    },
  } = useForm<UpdateDashboardValidationSchema>({
    resolver: zodResolver(updateDashboardSchema),
    defaultValues: {
      display_name: dashboard.display_name,
      desired_filters: dashboard.desired_filters || [],
    },
  });

  const watchedDesiredFilters = watch('desired_filters');

  const {
    mutate: updateDashboard,
    isPending: updateDashboardIsPending,
  } = useUpdateDashboard({
    dashboardId: dashboard.id,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['dashboards'],
        });

        queryClient.invalidateQueries({
          queryKey: ['dashboard', dashboard.id],
        });

        setIsOpen(false);
      },
      onError: FormTransformers.errorsTransformer({
        setError,
        setMessages,
      }),
    },
  });

  const onSubmit: SubmitHandler<UpdateDashboardValidationSchema> = (data) => updateDashboard(data);

  useEffect(() => {
    reset({
      display_name: dashboard.display_name,
      desired_filters: dashboard.desired_filters || [],
    });
  }, [isOpen]);

  const handleFilterChange = (filterValue: DashboardFilters, checked: boolean) => {
    const current = getValues('desired_filters') || [];
    if (checked) {
      setValue('desired_filters', [...current, filterValue]);
    } else {
      setValue('desired_filters', current.filter((f) => f !== filterValue));
    }
  };

  const filterEntries = Object.values(possibleFilters || {});

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title="Editar dashboard"
      className="rounded-lg overflow-hidden"
      bodyClassName="text-white-800"
      showClose={false}
      useCancellableFooter
      cancelButtonClassName="bg-white-200"
      confirmButtonClassName="bg-primary-blue"
      onConfirm={handleSubmit(onSubmit)}
      confirmButtonIsLoading={updateDashboardIsPending}
      preventAutoFocus
      body={(
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <TextField
            label="Nome do dashboard"
            markAsRequired
            {...register('display_name')}
            errorMessage={errors?.display_name?.message}
          />

          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <p className="font-bold text-base">
                Filtros desejáveis
              </p>

              <p className="text-white-400 text-xs">
                Esses são os filtros disponíveis de acordo com os gráficos selecionados.
              </p>
            </div>

            <div className="bg-white-100 rounded-md p-4 max-h-72 overflow-auto thin-scroll">
              {filterEntries.length > 0 ? (
                <div className="flex flex-col gap-3">
                  {filterEntries.map((filter) => (
                    <Checkbox
                      key={filter.value}
                      label={filter.label}
                      checked={watchedDesiredFilters?.includes(filter.value)}
                      onChange={(e) => handleFilterChange(filter.value, (e.target as HTMLInputElement).checked)}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-white-400 text-sm italic">
                  Não há filtros para serem selecionados
                </p>
              )}
            </div>
          </div>
        </form>
      )}
    />
  );
}

export default UpdateDashboard;
